import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Check, Close, ExpandLess, ExpandMore, NotInterested } from '@mui/icons-material';
import {
  REGULATION_GROUPS,
  REGULATION_STATUS,
  filterMarketRegulationsByCountries,
  findMarketRegulation,
  getCommentTopicLabel,
  marketHasExpandableNotes,
} from './utils/marketRegulations';

const DEFAULT_REGULATIONS_ACCENT = '#2e7d32';

function regulationsAccentSoft(accentColor) {
  return `${accentColor}14`;
}

const STATUS_STYLES = {
  [REGULATION_STATUS.authorized]: {
    label: 'Authorized',
    Icon: Check,
    color: '#2e7d32',
    bg: '#c8e6c9',
  },
  [REGULATION_STATUS.forbidden]: {
    label: 'Forbidden',
    Icon: Close,
    color: '#c62828',
    bg: '#ffcdd2',
  },
  [REGULATION_STATUS.unavailable]: {
    label: 'Not available',
    Icon: NotInterested,
    color: '#616161',
    bg: '#e0e0e0',
  },
};

const groupSeparatorSx = {
  borderLeft: '2px solid #cfd8dc',
};

const headerCellSx = {
  fontWeight: 600,
  fontSize: '0.65rem',
  lineHeight: 1.25,
  py: 0.6,
  px: 0.75,
  color: 'text.secondary',
  borderColor: '#cfd8dc',
  backgroundColor: '#eceff1',
  textAlign: 'center',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

const subHeaderCellSx = {
  ...headerCellSx,
  fontWeight: 600,
  fontSize: '0.7rem',
  color: 'text.primary',
  backgroundColor: '#f5f7f8',
};

const FLAG_IMG_DISPLAY_W = 10;
const FLAG_IMG_DISPLAY_H = 7;

const marketColumnSx = {
  width: 52,
  minWidth: 52,
  maxWidth: 56,
  whiteSpace: 'nowrap',
  px: 0.5,
  py: 0.5,
  borderColor: '#e0e0e0',
  verticalAlign: 'top',
};

const STATUS_COL_WIDTH = 52;

const REGULATION_COLUMN_WIDTHS = {
  bundlesHts: 52,
  bundlesHns: 52,
  bundlesAccessories: 84,
  sticksHts: 72,
  sticksHns: 72,
  promosDevices: 64,
  promosSticks: 52,
};

const getRegulationColumnWidth = (columnId) =>
  REGULATION_COLUMN_WIDTHS[columnId] ?? STATUS_COL_WIDTH;

const statusColumnSx = {
  width: STATUS_COL_WIDTH,
  minWidth: STATUS_COL_WIDTH,
  maxWidth: STATUS_COL_WIDTH,
  p: 0,
  borderColor: '#e0e0e0',
  verticalAlign: 'top',
  overflow: 'hidden',
  boxSizing: 'border-box',
};

const commentsColumnSx = {
  minWidth: 200,
  width: 'auto',
  borderColor: '#e0e0e0',
  verticalAlign: 'top',
  overflow: 'hidden',
  boxSizing: 'border-box',
  borderLeft: '2px solid #cfd8dc',
  backgroundColor: '#fff',
};

function normalizeIso2ForFlagcdn(code) {
  if (!code || typeof code !== 'string') return '';
  const c = code.trim().toLowerCase();
  const iso = c === 'uk' ? 'gb' : c;
  return iso.length === 2 ? iso : '';
}

function getCountryFlagImageUrl(iso2) {
  if (!iso2 || iso2.length !== 2) return '';
  const code = String(iso2).toLowerCase();
  return `https://flagcdn.com/w20/${code}.png`;
}

function MarketCountryCell({ code }) {
  const [flagHidden, setFlagHidden] = useState(false);
  const iso = normalizeIso2ForFlagcdn(code);
  useEffect(() => {
    setFlagHidden(false);
  }, [iso]);
  const showFlag = Boolean(iso && !flagHidden);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-start' }}>
      {showFlag ? (
        <Box
          component="img"
          src={getCountryFlagImageUrl(iso)}
          alt=""
          referrerPolicy="no-referrer"
          loading="lazy"
          onError={() => setFlagHidden(true)}
          sx={{
            width: FLAG_IMG_DISPLAY_W,
            height: FLAG_IMG_DISPLAY_H,
            objectFit: 'cover',
            borderRadius: '1px',
            flexShrink: 0,
            display: 'block',
          }}
        />
      ) : null}
      <Typography
        component="span"
        sx={{
          fontWeight: 600,
          fontSize: '0.7rem',
          lineHeight: 1.25,
          color: 'text.primary',
        }}
      >
        {code}
      </Typography>
    </Box>
  );
}

function StatusTick({ status }) {
  const cfg = STATUS_STYLES[status];
  if (!cfg) return null;
  const Icon = cfg.Icon;
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: cfg.bg,
        height: 28,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        px: 0.5,
      }}
    >
      <Icon sx={{ fontSize: 16, color: cfg.color }} />
    </Box>
  );
}

function RegulationLegend() {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
      {Object.values(REGULATION_STATUS).map((status) => {
        const cfg = STATUS_STYLES[status];
        const Icon = cfg.Icon;
        return (
          <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 22,
                height: 18,
                bgcolor: cfg.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 0.5,
              }}
            >
              <Icon sx={{ fontSize: 14, color: cfg.color }} />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.7rem' }}>
              {cfg.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function MarketNotes({ market }) {
  if (!marketHasExpandableNotes(market)) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {(market.comments || []).map((comment, idx) => (
        <Typography
          key={`${market.id}-comment-${idx}`}
          variant="caption"
          sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem', lineHeight: 1.35 }}
        >
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {getCommentTopicLabel(comment)}:{' '}
          </Box>
          {comment.text}
        </Typography>
      ))}
    </Box>
  );
}

function MarketCommentCell({ market, expanded, onToggle, accentColor = DEFAULT_REGULATIONS_ACCENT }) {
  const previewRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const comments = market.comments || [];

  const previewText = comments.map((comment, idx) => (
    <React.Fragment key={`${market.id}-preview-${idx}`}>
      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
        {getCommentTopicLabel(comment)}:{' '}
      </Box>
      {comment.text}
      {idx < comments.length - 1 ? ' ' : ''}
    </React.Fragment>
  ));

  const measureTruncated = useCallback(() => {
    if (expanded) {
      setIsTruncated(false);
      return;
    }
    const el = previewRef.current;
    if (!el) return;
    setIsTruncated(el.scrollWidth > el.clientWidth + 1);
  }, [expanded]);

  useEffect(() => {
    measureTruncated();
  }, [measureTruncated, comments]);

  useEffect(() => {
    const el = previewRef.current;
    if (!el || expanded) return undefined;
    const observer = new ResizeObserver(() => measureTruncated());
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded, measureTruncated]);

  const showExpandControl = expanded || isTruncated;

  const expandButtonSx = {
    textTransform: 'none',
    fontSize: '0.7rem',
    fontWeight: 600,
    minWidth: 0,
    px: 0,
    py: 0,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    lineHeight: 1.35,
    verticalAlign: 'baseline',
    color: accentColor,
    '&:hover': {
      backgroundColor: regulationsAccentSoft(accentColor),
      color: accentColor,
    },
    '& .MuiButton-endIcon': {
      color: accentColor,
    },
  };

  return (
    <Box sx={{ py: 0.25, px: 0.5, minWidth: 0, overflow: 'hidden' }}>
      {expanded ? (
        <>
          <MarketNotes market={market} />
          {showExpandControl ? (
            <Button
              size="small"
              variant="text"
              onClick={onToggle}
              endIcon={<ExpandLess sx={{ fontSize: 14 }} />}
              sx={{ ...expandButtonSx, mt: 0.25, justifyContent: 'flex-start' }}
            >
              Show less
            </Button>
          ) : null}
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.5,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Typography
            ref={previewRef}
            variant="caption"
            component="span"
            sx={{
              color: 'text.secondary',
              fontSize: '0.7rem',
              lineHeight: 1.35,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: '1 1 auto',
              minWidth: 0,
            }}
          >
            {previewText}
          </Typography>
          {showExpandControl ? (
            <Button
              size="small"
              variant="text"
              onClick={onToggle}
              endIcon={<ExpandMore sx={{ fontSize: 14 }} />}
              sx={expandButtonSx}
            >
              Show more...
            </Button>
          ) : null}
        </Box>
      )}
    </Box>
  );
}

function RegulationTable({
  markets,
  showMarketColumn,
  commentsOpenByMarket,
  onToggleComments,
  accentColor = DEFAULT_REGULATIONS_ACCENT,
}) {
  const statusColumns = REGULATION_GROUPS.flatMap((group) => group.columns);
  const tableMinWidth =
    (showMarketColumn ? STATUS_COL_WIDTH : 0) +
    statusColumns.reduce((sum, column) => sum + getRegulationColumnWidth(column.id), 0) +
    200;

  return (
    <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
      <Table
        size="small"
        sx={{
          width: '100%',
          minWidth: tableMinWidth,
          borderCollapse: 'separate',
          tableLayout: 'fixed',
        }}
      >
        <colgroup>
          {showMarketColumn && <col style={{ width: STATUS_COL_WIDTH }} />}
          {statusColumns.map((column) => (
            <col key={column.id} style={{ width: getRegulationColumnWidth(column.id) }} />
          ))}
          <col />
        </colgroup>
        <TableHead>
          <TableRow>
            {showMarketColumn && (
              <TableCell rowSpan={2} sx={{ ...headerCellSx, ...marketColumnSx, textAlign: 'left', whiteSpace: 'nowrap' }}>
                Market
              </TableCell>
            )}
            {REGULATION_GROUPS.map((group, groupIndex) => (
              <TableCell
                key={group.id}
                colSpan={group.columns.length}
                sx={{
                  ...headerCellSx,
                  whiteSpace: 'nowrap',
                  wordBreak: 'normal',
                  minWidth: group.columns.reduce(
                    (sum, column) => sum + getRegulationColumnWidth(column.id),
                    0
                  ),
                  ...(groupIndex > 0 ? groupSeparatorSx : {}),
                }}
              >
                {group.label}
              </TableCell>
            ))}
            <TableCell rowSpan={2} sx={{ ...headerCellSx, ...commentsColumnSx, textAlign: 'left' }}>
              Comments
            </TableCell>
          </TableRow>
          <TableRow>
            {REGULATION_GROUPS.flatMap((group, groupIndex) =>
              group.columns.map((column, columnIndex) => (
                <TableCell
                  key={column.id}
                  sx={{
                    ...subHeaderCellSx,
                    width: getRegulationColumnWidth(column.id),
                    minWidth: getRegulationColumnWidth(column.id),
                    maxWidth: getRegulationColumnWidth(column.id),
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    ...(groupIndex > 0 && columnIndex === 0 ? groupSeparatorSx : {}),
                  }}
                >
                  {column.label}
                </TableCell>
              ))
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {markets.map((market) => {
            const hasNotes = marketHasExpandableNotes(market);
            const commentsOpen = Boolean(commentsOpenByMarket[market.id]);
            return (
              <TableRow key={market.id}>
                {showMarketColumn && (
                  <TableCell sx={marketColumnSx}>
                    <MarketCountryCell code={market.code} />
                  </TableCell>
                )}
                {REGULATION_GROUPS.flatMap((group, groupIndex) =>
                  group.columns.map((column, columnIndex) => (
                    <TableCell
                      key={column.id}
                      sx={{
                        ...statusColumnSx,
                        width: getRegulationColumnWidth(column.id),
                        minWidth: getRegulationColumnWidth(column.id),
                        maxWidth: getRegulationColumnWidth(column.id),
                        ...(groupIndex > 0 && columnIndex === 0 ? groupSeparatorSx : {}),
                      }}
                    >
                      <StatusTick status={market.statuses[column.id]} />
                    </TableCell>
                  ))
                )}
                <TableCell sx={{ ...commentsColumnSx, py: 0.25, px: 0.75 }}>
                  {hasNotes ? (
                    <MarketCommentCell
                      market={market}
                      expanded={commentsOpen}
                      onToggle={() => onToggleComments(market.id)}
                      accentColor={accentColor}
                    />
                  ) : (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function MarketRegulationsPanel({
  variant = 'market',
  selectedMarket,
  selectedCountries = [],
  accentColor = DEFAULT_REGULATIONS_ACCENT,
}) {
  const isHq = variant === 'hq';
  const [overviewOpen, setOverviewOpen] = useState(!isHq);
  const [commentsOpenByMarket, setCommentsOpenByMarket] = useState({});

  const markets = useMemo(() => {
    if (isHq) return filterMarketRegulationsByCountries(selectedCountries);
    const match = findMarketRegulation(selectedMarket);
    return match ? [match] : [];
  }, [isHq, selectedMarket, selectedCountries]);

  if (!markets.length) return null;

  const toggleComments = (marketId) => {
    setCommentsOpenByMarket((prev) => ({ ...prev, [marketId]: !prev[marketId] }));
  };

  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, borderColor: '#e0e0e0', backgroundColor: '#f8f9fa' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.primary' }}>
          {isHq ? 'Market regulations' : `Market regulations · ${markets[0].code}`}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setOverviewOpen((open) => !open)}
          endIcon={overviewOpen ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 30,
            color: accentColor,
            borderColor: accentColor,
            '&:hover': {
              borderColor: accentColor,
              backgroundColor: regulationsAccentSoft(accentColor),
            },
          }}
        >
          {overviewOpen ? 'Hide market regulations' : 'Show market regulations'}
        </Button>
      </Box>
      <Collapse in={overviewOpen} timeout="auto" unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.25 }}>
          <RegulationLegend />
          <RegulationTable
            markets={markets}
            showMarketColumn={isHq || markets.length > 1}
            commentsOpenByMarket={commentsOpenByMarket}
            onToggleComments={toggleComments}
            accentColor={accentColor}
          />
        </Box>
      </Collapse>
    </Paper>
  );
}
